import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { currentUrl } from '../../url';
const userData  = localStorage.getItem("user" )
const user = userData ? JSON.parse(userData) : {}; 
const step = user?.step
const id = user?.id
// Токен будет читаться при каждом запросе, а не при загрузке модуля
//   ? localStorage.getItem("salon_id")
const restaurant_id = localStorage.getItem("salon_id")
  ? localStorage.getItem("restaurant_id")
  : user?.restaurant_id

const salon_id = user?.salon_id || user?.restaurant_id
const isRestaurant = user?.restaurant?.direction_id === 6

interface IMedia {
  original_url: string
  collection_name: string
}

type Setting = {
  id: number
  name: string
  surname: string
  phone: string
  deep_link: string
  description: string
  street: string
  building: string
  website: string
  facebook: string
  instagram: string
  avatar: string
  city_id: number
  direction_id: number
  status_id: number
  media: IMedia[]
  type?: 1 | 2
  hide_client_phone: 0 | 1
}

export type newSetting = Omit<
  Setting,
  "avatar" | "media" | "type" | "status_id"
>

type Media = {
  file: File
  collection_name: string
  model_id: number
  id: number
}

type SettingState = {
  list: Setting
  loading: boolean
  error: string | null
  postLoading: boolean
  postError: boolean
  mediaLoading: boolean
  mediaError: boolean
  status_id: number
}

export const fetchSettings = createAsyncThunk<
  Setting,
  string,
  { rejectValue: string }
>(
  "settings/fetchSettings",

  async function (_, { rejectWithValue }) {
    try {
      // Auto-refresh salon_id from localStorage in case it's outdated
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : {};
      const current_salon_id = user?.salon_id || user?.restaurant_id || localStorage.getItem("salon_id");
      
      // Читаем токен при каждом запросе, чтобы использовать актуальное значение
      const accessToken = localStorage.getItem("accessToken");
      
      // Check if required data exists
      if (!accessToken) {
        return rejectWithValue("Токен доступа отсутствует. Пожалуйста, авторизуйтесь снова.");
      }
      
      if (!current_salon_id) {
        // Try to get salon_id from different sources
        const fallback_salon_id = localStorage.getItem("restaurant_id") || 
                                 (user?.restaurant_id) ||
                                 (user?.salon_id);
        
        if (!fallback_salon_id) {
          return rejectWithValue("ID салона отсутствует. Система пытается восстановить данные...");
        }
      }

      const final_salon_id = current_salon_id || localStorage.getItem("salon_id");
      const isRestaurant = user?.restaurant?.direction_id === 6;

      const url = isRestaurant
        ? `${currentUrl}/master/restaurants/${final_salon_id}`
        : `${currentUrl}/master/salons/${final_salon_id}`

      // Убеждаемся, что токен имеет правильный формат (Bearer префикс)
      const token = accessToken.startsWith("Bearer ") 
        ? accessToken 
        : `Bearer ${accessToken}`

      const response = await fetch(url, {
        headers: {
          Authorization: token,
        },
      })

      // Handle different HTTP status codes
      if (response.status === 401) {
        // Token might be expired, try to refresh
        return rejectWithValue("Ошибка авторизации. Система пытается восстановить подключение...");
      }
      
      if (response.status === 404) {
        // Try alternative endpoint
        return rejectWithValue("Данные не найдены. Система пытается восстановить данные...");
      }
      
      if (response.status === 500) {
        return rejectWithValue("Ошибка сервера. Система пытается восстановить подключение...");
      }

      if (!response.ok) {
        // Try to get error message from backend response
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            // For temporary errors, don't show to user, just retry
            if (errorData.message.includes("временно") || errorData.message.includes("попробуйте")) {
              return rejectWithValue("Система пытается восстановить подключение...");
            }
            return rejectWithValue(errorData.message);
          }
          if (errorData && errorData.error) {
            return rejectWithValue(errorData.error);
          }
        } catch (e) {
          // If we can't parse JSON, continue with default message
        }
        
        return rejectWithValue(`Ошибка при загрузке данных. Система пытается восстановить подключение...`);
      }

      const data = await response.json()

      // Validate required data in response
      if (!data || typeof data !== 'object') {
        return rejectWithValue("Получены некорректные данные. Система пытается восстановить подключение...");
      }

      const processedData: Setting | Omit<Setting, "type"> = {
        id: data.id || 0,
        name: data.name || "",
        surname: data.surname || "",
        phone: data.phone || "",
        deep_link: data.deep_link || "",
        description: data.description || "",
        street: data.street || "",
        building: data.building || "",
        website: data.website || "",
        facebook: data.facebook || "",
        instagram: data.instagram || "",
        avatar: data.avatar && data.avatar.length > 0 
          ? data.avatar[data.avatar.length - 1]?.original_url || "" 
          : "",
        city_id: data.city_id || 0,
        direction_id: data.direction_id || 0,
        status_id: data.status_id,
        hide_client_phone: data.hide_client_phone === 1 ? 1 : 0,
        media: data.media && Array.isArray(data.media) 
          ? data.media.map(
              (item:IMedia): IMedia => ({
                original_url: item.original_url,
                collection_name: item.collection_name,
              })
            )
          : [],
        ...(isRestaurant ? {} : { type: data.salon_type?.id || 0 }), // Exclude `type` for restaurants
      }

      return processedData
    } catch (error) {
      // Network error or other unexpected errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return rejectWithValue("Система пытается восстановить подключение...");
      }
      return rejectWithValue("Система пытается восстановить подключение...");
    }
  }
)

export const postSettings = createAsyncThunk<
  newSetting,
  newSetting,
  { rejectValue: boolean }
>(
  "settings/postSettings",
  async function ({ id, ...body }, { rejectWithValue }) {
    body.phone = body?.phone?.replace(/-/g, "").slice(-9)
    id = salon_id || id
    try {
      // Читаем токен при каждом запросе, чтобы использовать актуальное значение
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        toast.error("Токен доступа отсутствует. Пожалуйста, авторизуйтесь снова.");
        return rejectWithValue(true);
      }

      const url = isRestaurant
        ? `${currentUrl}/master/restaurants/${id}`
        : `${currentUrl}/master/salons/${id}`

      // Убеждаемся, что токен имеет правильный формат (Bearer префикс)
      const token = accessToken.startsWith("Bearer ") 
        ? accessToken 
        : `Bearer ${accessToken}`

      const res = await fetch(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        if (res.status === 422) {
          try {
            const errData = await res.json()
            const message =
              errData?.message || "Deep link уже используется другим салоном"
            toast.error(message)
          } catch (_) {
            toast.error("Deep link уже используется другим салоном")
          }
        } else {
          toast.error("Ошибка при изменении настроек")
        }
        return rejectWithValue(true)
      }

      toast.success("Настройки изменены", { position: "bottom-right" })
      const data = await res.json()
      const processedData: newSetting = {
        id: data.id,
        name: data.name,
        surname: data.surname,
        deep_link: data.deep_link,
        phone: data.phone,
        description: data.description,
        street: data.street,
        building: data.building,
        website: data.website,
        facebook: data.facebook,
        instagram: data.instagram,
        city_id: data.city_id,
        direction_id: data.direction_id,
        hide_client_phone: data.hide_client_phone === 1 ? 1 : 0,
      }

      return processedData
    } catch (err) {
      console.error(err)
      toast.error("Ошибка при изменении настроек")
      return rejectWithValue(true);    }
  }
)

export const postMedia = createAsyncThunk<any, Media, { rejectValue: boolean }>(
  "settings/postMedia",
  async function ({ model_id, file, collection_name }, { rejectWithValue }) {
    try {
      // Читаем токен при каждом запросе, чтобы использовать актуальное значение
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        toast.error("Токен доступа отсутствует. Пожалуйста, авторизуйтесь снова.");
        return rejectWithValue(true);
      }

      const formData = new FormData()
      formData.append("file", file)
      formData.append("collection_name", collection_name)
      formData.append("model_id", String(model_id))
      model_id = salon_id || model_id

      const isRestaurant = user?.restaurant.direction_id === 1
      const url = isRestaurant
        ? `${currentUrl}/master/restaurants/${model_id}/update-image` 
        : `${currentUrl}/master/salons/${model_id}/update-image`

      // Убеждаемся, что токен имеет правильный формат (Bearer префикс)
      const token = accessToken.startsWith("Bearer ") 
        ? accessToken 
        : `Bearer ${accessToken}`

      const res = await fetch(
        // `${currentUrl}/master/salons/${model_id}/update-image` ,
        url,
        {
          headers: {
            Authorization: token,
          },
          method: "POST",
          body: formData,
        }
      )

      return res.json()
    } catch (err) {
      rejectWithValue(true)
      toast.error("Ошибка при изменении медиафайлов")
    }
  }
)

const initialState: SettingState = {
  list: {
    id: 0,
    name: "",
    surname:"",
    phone: "",
    deep_link: "",
    description: "",
    street: "",
    building: "",
    website: "",
    facebook: "",
    instagram: "",
    avatar: "",
    city_id: 0,
    direction_id: 0,
    status_id: 0,
    hide_client_phone: 0,
    media: [],
    type: 1,
  },
  status_id: 1,
  loading: false,
  error: null,
  postError: false,
  postLoading: false,
  mediaError: false,
  mediaLoading: false,
}

const settingsSlice = createSlice({
  name: "settingsSlice",
  initialState,
  reducers: {
    setStatusId(state, action) {
      state.status_id = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false
        state.list = action.payload
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false
        state.error = typeof action.payload === "string" ? action.payload : null;
      })
      .addCase(postSettings.pending, (state) => {
        state.postError = false
        state.postLoading = true
      })
      .addCase(postSettings.fulfilled, (state) => {
        state.postLoading = false
      })
      .addCase(postSettings.rejected, (state) => {
        state.postError = true
        state.postLoading = false
      })
      .addCase(postMedia.pending, (state) => {
        state.mediaLoading = true
        state.mediaError = false
      })
      .addCase(postMedia.fulfilled, (state) => {
        state.mediaLoading = false
      })
      .addCase(postMedia.rejected, (state) => {
        state.mediaError = true
        state.mediaLoading = false
      })
  },
})

export const { setStatusId } = settingsSlice.actions

export default settingsSlice.reducer